import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { SecureImage } from "@/components/SecureImage";
import { formatINR, formatDateTime, formatDate } from "@/lib/format";
import {
  Loader2, ChevronLeft, User2, ShieldCheck, Users, Landmark, CheckCircle2, XCircle,
  Info, Banknote, FileText, Wallet, Ban,
} from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value, mono }) => (
  <div className="py-1.5">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-sm font-medium ${mono ? "font-mono" : ""}`}>{value || "-"}</p>
  </div>
);

const Section = ({ title, Icon, children }) => (
  <Card className="p-5 sm:p-6 ip-card-shadow">
    <div className="flex items-center gap-2 text-brand mb-3"><Icon className="h-5 w-5" /><h3 className="font-semibold">{title}</h3></div>
    {children}
  </Card>
);

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [offer, setOffer] = useState({
    approved_amount: "", interest_rate: "", processing_fee: "", gst: "", tenure: "",
    tenure_unit: "Months", repayment_amount: "", disbursement_amount: "", repayment_frequency: "One-time",
    repayment_date: "", late_penalty: "",
  });

  const load = async () => {
    const { data } = await api.get(`/admin/applications/${id}`);
    setData(data);
    if (data.offer) {
      const o = data.offer;
      setOffer({
        approved_amount: o.approved_amount, interest_rate: o.interest_rate, processing_fee: o.processing_fee,
        gst: o.gst, tenure: o.tenure, tenure_unit: o.tenure_unit, repayment_amount: o.repayment_amount,
        disbursement_amount: o.disbursement_amount, repayment_frequency: o.repayment_frequency,
        repayment_date: o.repayment_date, late_penalty: o.late_penalty,
      });
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const setO = (k) => (e) => setOffer({ ...offer, [k]: e.target.value });

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitOffer = () => {
    const req = ["approved_amount", "interest_rate", "processing_fee", "gst", "tenure", "repayment_amount", "disbursement_amount", "repayment_date", "late_penalty"];
    for (const k of req) if (offer[k] === "" || offer[k] === null) { toast.error("Please fill all offer fields"); return; }
    const payload = {
      approved_amount: parseFloat(offer.approved_amount), interest_rate: parseFloat(offer.interest_rate),
      processing_fee: parseFloat(offer.processing_fee), gst: parseFloat(offer.gst), tenure: parseInt(offer.tenure),
      tenure_unit: offer.tenure_unit, repayment_amount: parseFloat(offer.repayment_amount),
      disbursement_amount: parseFloat(offer.disbursement_amount), repayment_frequency: offer.repayment_frequency,
      repayment_date: offer.repayment_date, late_penalty: parseFloat(offer.late_penalty),
    };
    act(() => api.post(`/admin/applications/${id}/offer`, payload), "Offer created & application approved");
  };

  // auto suggest repayment = approved + fees + gst + interest amount (simple helper)
  const autoCalc = () => {
    const amt = parseFloat(offer.approved_amount) || 0;
    const interest = (amt * (parseFloat(offer.interest_rate) || 0)) / 100;
    const pf = parseFloat(offer.processing_fee) || 0;
    const gst = parseFloat(offer.gst) || 0;
    const repay = Math.round(amt + interest);
    const disb = Math.round(amt - pf - gst);
    setOffer((o) => ({ ...o, repayment_amount: String(repay), disbursement_amount: String(disb) }));
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const app = data.application;
  const cust = data.customer;
  const o = data.offer;
  const canSetOffer = ["UNDER_REVIEW", "INFO_REQUESTED", "APPROVED"].includes(app.status);
  const canDisburse = o && o.status === "confirmed" && app.status === "DISBURSE_PENDING";

  return (
    <div className="space-y-6 max-w-5xl">
      <button onClick={() => nav("/admin")} className="text-sm text-muted-foreground hover:text-foreground flex items-center"><ChevronLeft className="h-4 w-4" /> Back to dashboard</button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono text-2xl font-extrabold tracking-tight" data-testid="detail-reference">{app.reference_number}</p>
          <p className="text-sm text-muted-foreground">Submitted {formatDateTime(app.submitted_at)}</p>
        </div>
        <StatusBadge status={app.status} />
      </div>

      {["REJECTED", "CANCELLED"].includes(app.status) && (
        <Card className="p-4 ip-card-shadow border-2 border-rose-200" data-testid="admin-rejection-info">
          <div className="flex items-center gap-2 text-rose-700 mb-2"><Ban className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">{app.status === "CANCELLED" ? "Cancelled" : "Rejected"} — 7-day re-apply lock</span></div>
          <div className="grid grid-cols-2 gap-x-6">
            <Field label="Rejected date" value={formatDateTime(app.rejected_at)} />
            <Field label="Re-apply available" value={formatDateTime(app.reapply_available_at)} />
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Personal Details" Icon={User2}>
          <div className="grid grid-cols-2 gap-x-6">
            <Field label="Full name" value={app.personal.full_name} />
            <Field label="Date of birth" value={app.personal.dob} />
            <Field label="Gender" value={app.personal.gender} />
            <Field label="Monthly income" value={formatINR(app.personal.monthly_income)} mono />
            <Field label="Employment" value={app.personal.employment_type} />
            <Field label="Pincode" value={app.personal.pincode} mono />
            <div className="col-span-2"><Field label="Address" value={`${app.personal.address}, ${app.personal.city}, ${app.personal.state}`} /></div>
          </div>
          <div className="mt-2 pt-2 border-t border-border/60 grid grid-cols-2 gap-x-6">
            <Field label="Email" value={cust.email} />
            <Field label="Phone" value={cust.phone} mono />
          </div>
        </Section>

        <Section title="KYC & Documents" Icon={ShieldCheck}>
          <div className="grid grid-cols-2 gap-x-6 mb-3">
            <Field label="Aadhaar number" value={app.kyc.aadhaar_number} mono />
            <Field label="PAN number" value={app.kyc.pan_number} mono />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><p className="text-xs text-muted-foreground mb-1">Aadhaar</p><SecureImage fileId={app.kyc.aadhaar_file_id} testId="kyc-aadhaar-img" className="w-full aspect-[3/4] object-cover rounded-lg border border-border" /></div>
            <div><p className="text-xs text-muted-foreground mb-1">PAN</p><SecureImage fileId={app.kyc.pan_file_id} testId="kyc-pan-img" className="w-full aspect-[3/4] object-cover rounded-lg border border-border" /></div>
            <div><p className="text-xs text-muted-foreground mb-1">Selfie</p><SecureImage fileId={app.kyc.selfie_file_id} testId="kyc-selfie-img" className="w-full aspect-[3/4] object-cover rounded-lg border border-border" /></div>
          </div>
        </Section>

        <Section title="Reference Contacts" Icon={Users}>
          <div className="space-y-2">
            {app.references.map((r, i) => (
              <div key={`${r.phone}-${r.name}-${i}`} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <div><p className="text-sm font-medium">{r.name}</p><p className="text-xs text-muted-foreground">{r.relationship || "Reference"}</p></div>
                <span className="font-mono text-sm">{r.phone}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Bank Details" Icon={Landmark}>
          <div className="grid grid-cols-2 gap-x-6">
            <Field label="Account holder" value={app.bank.account_holder} />
            <Field label="Bank name" value={app.bank.bank_name} />
            <Field label="Account number" value={app.bank.account_number} mono />
            <Field label="IFSC" value={app.bank.ifsc} mono />
          </div>
          <div className="mt-2">
            <p className="text-xs text-muted-foreground mb-1">Bank proof</p>
            <SecureImage fileId={app.bank.proof_file_id} testId="bank-proof-img" className="w-full max-w-xs aspect-video object-cover rounded-lg border border-border" />
          </div>
        </Section>
      </div>

      {/* Loan Offer */}
      {app.status !== "REJECTED" && (
        <Section title="Loan Offer" Icon={Wallet}>
          {o && ["disbursed", "repaid"].includes(o.status) ? (
            <div className="grid sm:grid-cols-3 gap-x-6">
              <Field label="Approved amount" value={formatINR(o.approved_amount)} mono />
              <Field label="Disbursement amount" value={formatINR(o.disbursement_amount)} mono />
              <Field label="Total repayment" value={formatINR(o.repayment_amount)} mono />
              <Field label="Amount paid" value={formatINR(o.amount_paid)} mono />
              <Field label="Outstanding" value={formatINR(o.outstanding)} mono />
              <Field label="Offer status" value={<StatusBadge status={o.status} />} />
            </div>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-4">Enter the loan terms. These are server-validated and cannot be modified by the customer.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label className="mb-1.5 block text-xs">Approved amount (₹)</Label><Input data-testid="offer-approved-amount" value={offer.approved_amount} onChange={setO("approved_amount")} inputMode="numeric" /></div>
                <div><Label className="mb-1.5 block text-xs">Interest rate (%)</Label><Input data-testid="offer-interest-rate" value={offer.interest_rate} onChange={setO("interest_rate")} inputMode="numeric" /></div>
                <div><Label className="mb-1.5 block text-xs">Processing fee (₹)</Label><Input data-testid="offer-processing-fee" value={offer.processing_fee} onChange={setO("processing_fee")} inputMode="numeric" /></div>
                <div><Label className="mb-1.5 block text-xs">GST (₹)</Label><Input data-testid="offer-gst" value={offer.gst} onChange={setO("gst")} inputMode="numeric" /></div>
                <div><Label className="mb-1.5 block text-xs">Tenure</Label><Input data-testid="offer-tenure" value={offer.tenure} onChange={setO("tenure")} inputMode="numeric" /></div>
                <div><Label className="mb-1.5 block text-xs">Tenure unit</Label>
                  <Select value={offer.tenure_unit} onValueChange={(v) => setOffer({ ...offer, tenure_unit: v })}>
                    <SelectTrigger data-testid="offer-tenure-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Days">Days</SelectItem>
                      <SelectItem value="Weeks">Weeks</SelectItem>
                      <SelectItem value="Months">Months</SelectItem>
                      <SelectItem value="Years">Years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-xs">Disbursement amount (₹)</Label><Input data-testid="offer-disbursement-amount" value={offer.disbursement_amount} onChange={setO("disbursement_amount")} inputMode="numeric" /></div>
                <div><Label className="mb-1.5 block text-xs">Total repayment (₹)</Label><Input data-testid="offer-repayment-amount" value={offer.repayment_amount} onChange={setO("repayment_amount")} inputMode="numeric" /></div>
                <div><Label className="mb-1.5 block text-xs">Late penalty (₹)</Label><Input data-testid="offer-late-penalty" value={offer.late_penalty} onChange={setO("late_penalty")} inputMode="numeric" /></div>
                <div><Label className="mb-1.5 block text-xs">Repayment frequency</Label>
                  <Select value={offer.repayment_frequency} onValueChange={(v) => setOffer({ ...offer, repayment_frequency: v })}>
                    <SelectTrigger data-testid="offer-repayment-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="One-time">One-time</SelectItem>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block text-xs">Repayment date</Label><Input type="date" data-testid="offer-repayment-date" value={offer.repayment_date} onChange={setO("repayment_date")} /></div>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button type="button" variant="outline" onClick={autoCalc} data-testid="offer-autocalc">Auto-calculate repayment & disbursement</Button>
                {canSetOffer && (
                  <Button onClick={submitOffer} disabled={busy} className="bg-brand" data-testid="offer-submit-button">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    {o ? "Update Offer" : "Approve & Create Offer"}
                  </Button>
                )}
              </div>
              {o && <p className="mt-3 text-sm">Current offer status: <StatusBadge status={o.status} /></p>}
            </>
          )}
        </Section>
      )}

      {/* Actions */}
      <Section title="Decision & Actions" Icon={FileText}>
        <Textarea placeholder="Optional note (shown to customer for 'Request Info' / recorded for reject)" value={note} onChange={(e) => setNote(e.target.value)} className="mb-4" data-testid="admin-action-note" />
        <div className="flex flex-wrap gap-3">
          {["UNDER_REVIEW", "INFO_REQUESTED", "APPROVED"].includes(app.status) && (
            <>
              <Button variant="outline" onClick={() => act(() => api.post(`/admin/applications/${id}/request-info`, { note }), "Requested more information")} disabled={busy} data-testid="action-request-info">
                <Info className="h-4 w-4 mr-2" /> Request More Info
              </Button>
              <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => act(() => api.post(`/admin/applications/${id}/reject`, { note }), "Application rejected")} disabled={busy} data-testid="action-reject">
                <XCircle className="h-4 w-4 mr-2" /> Reject
              </Button>
            </>
          )}
          {canDisburse && (
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(() => api.post(`/admin/applications/${id}/disburse`), "Loan marked as disbursed")} disabled={busy} data-testid="admin-disburse-btn">
              <Banknote className="h-4 w-4 mr-2" /> DISBURSE
            </Button>
          )}
          {["APPROVED", "DISBURSE_PENDING"].includes(app.status) && (
            <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => act(() => api.post(`/admin/applications/${id}/cancel`, { note }), "Loan cancelled")} disabled={busy} data-testid="action-cancel">
              <XCircle className="h-4 w-4 mr-2" /> Cancel / Reject Loan
            </Button>
          )}
        </div>
      </Section>

      {/* Customer application history (all previous + current) */}
      <Section title="Customer Application History" Icon={FileText}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-medium">Application #</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium hidden sm:table-cell">Submitted</th>
                <th className="py-2 pr-4 font-medium hidden md:table-cell">Rejected</th>
                <th className="py-2 pr-4 font-medium hidden md:table-cell">Re-apply Available</th>
              </tr>
            </thead>
            <tbody>
              {(data.customer_applications || []).map((a) => (
                <tr key={a.id} className="border-b border-border/60" data-testid={`cust-app-${a.reference_number}`}>
                  <td className="py-2.5 pr-4 font-mono font-semibold">{a.reference_number}</td>
                  <td className="py-2.5 pr-4"><StatusBadge status={a.status} /></td>
                  <td className="py-2.5 pr-4 text-muted-foreground hidden sm:table-cell">{formatDateTime(a.submitted_at)}</td>
                  <td className="py-2.5 pr-4 text-muted-foreground hidden md:table-cell">{a.rejected_at ? formatDateTime(a.rejected_at) : "-"}</td>
                  <td className="py-2.5 pr-4 text-muted-foreground hidden md:table-cell">{a.reapply_available_at ? formatDateTime(a.reapply_available_at) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* History */}
      <Section title="Application History" Icon={FileText}>
        <div className="space-y-2">
          {data.history.map((h) => (
            <div key={h.id} className="flex items-center justify-between text-sm border-b border-border/60 py-2 last:border-0">
              <div><StatusBadge status={h.status} /> <span className="text-muted-foreground ml-2">{h.note}</span></div>
              <span className="text-xs text-muted-foreground">{formatDateTime(h.at)}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Repayments for this app */}
      {data.repayments.length > 0 && (
        <Section title="Repayment Submissions" Icon={Banknote}>
          <div className="space-y-2">
            {data.repayments.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm border-b border-border/60 py-2 last:border-0">
                <div><span className="font-mono font-semibold">{formatINR(r.amount)}</span> <span className="text-muted-foreground ml-2">UTR {r.utr}</span></div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
